'use client';

import { useEffect, useRef } from 'react';
import { useRouter } from 'next/navigation';
import toast from 'react-hot-toast';

const CHECK_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Detects when a logged-in session expires without the user noticing.
 *
 * Triggers on two events while `enabled` is true:
 *   1. `visibilitychange` — the user returns to this tab after being away
 *   2. A periodic interval — every 5 minutes while the tab is active
 *
 * When expiry is detected the user sees a toast and is redirected to `/`.
 */
export function useSessionExpiry(enabled: boolean) {
  const router = useRouter();
  const expiredRef = useRef(false);

  useEffect(() => {
    if (!enabled) return;

    // Reset the guard each time the hook becomes active (fresh login).
    expiredRef.current = false;

    const checkSession = async () => {
      if (expiredRef.current) return;
      try {
        const res = await fetch('/api/auth/session', {
          credentials: 'include',
          cache: 'no-store',
        });
        const data: { authenticated?: boolean } = await res.json();
        if (!data.authenticated) {
          expiredRef.current = true;
          toast.error('Your session has expired. Please sign in again.', {
            id: 'session-expired',
            duration: 6000,
          });
          router.replace('/');
        }
      } catch {
        // Network error — don't redirect; the user may just be offline temporarily.
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkSession();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    const intervalId = setInterval(checkSession, CHECK_INTERVAL_MS);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearInterval(intervalId);
    };
  }, [enabled, router]);
}
